import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import {
  useHMSStore,
  useHMSActions,
  selectPeers,
  selectIsConnectedToRoom,
  selectVideoTrackByPeerID,
  selectTracksMap,
} from '@100mslive/react-sdk';
import { colors } from '../constants/colors';

interface VideoViewerProps {
  authToken: string | null;
  userName?: string;
  thumbnailUrl?: string | null;
  broadcasterPaused?: boolean;
}

export function VideoViewer({ authToken, userName = 'Viewer', thumbnailUrl, broadcasterPaused }: VideoViewerProps) {
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const peers = useHMSStore(selectPeers);
  const tracksMap = useHMSStore(selectTracksMap);
  const [videoError, setVideoError] = useState<string | null>(null);
  const isConnectedRef = useRef(isConnected);
  isConnectedRef.current = isConnected;

  // Find the broadcaster with an active (enabled) video track.
  // With multi-device broadcast, multiple peers may have the broadcaster role
  // (e.g. iOS + web sessions for the same seller). We need to find the peer
  // whose video track is actually enabled, not just the first with any track.
  const broadcasters = peers.filter((peer) => peer.roleName === 'host' || peer.roleName === 'broadcaster');

  const activeBroadcaster = useMemo(() => {
    // Prefer a broadcaster whose video track is enabled (actively streaming)
    const withEnabledVideo = broadcasters.find((peer) => {
      if (!peer.videoTrack) return false;
      const track = tracksMap[peer.videoTrack];
      return track?.enabled;
    });
    if (withEnabledVideo) return withEnabledVideo;
    // Fall back to any broadcaster with a video track, then any broadcaster
    return broadcasters.find((peer) => peer.videoTrack) || broadcasters[0];
  }, [broadcasters, tracksMap]);

  const videoTrack = useHMSStore(selectVideoTrackByPeerID(activeBroadcaster?.id));
  const activeVideoTrack = videoTrack?.enabled ? videoTrack : null;

  useEffect(() => {
    if (!authToken) return;

    const joinRoom = async () => {
      try {
        await hmsActions.join({
          authToken,
          userName,
          settings: {
            isAudioMuted: true,
            isVideoMuted: true,
          },
          rememberDeviceSelection: false,
          autoManageWakeLock: false,
        });
      } catch (e) {
        console.error('Failed to join video room:', e);
        setVideoError('Video unavailable - you can still bid!');
      }
    };

    joinRoom();

    return () => {
      if (isConnectedRef.current) {
        hmsActions.leave().catch(() => {
          // Ignore leave errors
        });
      }
    };
  }, [authToken, hmsActions]);

  const blurRef = useRef<HTMLVideoElement>(null);

  // Mirror the main video's srcObject to the blurred background element
  // instead of calling attachVideo twice (which can create duplicate audio sinks)
  const mainVideoRef = useCallback((el: HTMLVideoElement | null) => {
    if (el && activeVideoTrack) {
      hmsActions.attachVideo(activeVideoTrack.id, el);
      const sync = () => {
        if (blurRef.current && el.srcObject) {
          blurRef.current.srcObject = el.srcObject;
        }
      };
      // srcObject may already be set or arrive async
      sync();
      el.addEventListener('loadedmetadata', sync, { once: true });
    }
  }, [activeVideoTrack, hmsActions]);

  const renderPlaceholder = (message: string, subtext?: string) => (
    <div style={styles.placeholder}>
      {thumbnailUrl && (
        <img src={thumbnailUrl} alt="" style={styles.thumbnailBg} />
      )}
      <p style={styles.placeholderText}>{message}</p>
      {subtext && <p style={styles.subtext}>{subtext}</p>}
    </div>
  );

  if (!authToken || videoError) {
    return renderPlaceholder(
      videoError || 'Video stream unavailable',
      'You can still participate in the auction below',
    );
  }

  if (!isConnected) {
    return renderPlaceholder('Connecting to stream...');
  }

  if (!activeBroadcaster) {
    return renderPlaceholder('Waiting for host to start streaming...');
  }

  return (
    <div style={styles.container}>
      {activeVideoTrack ? (
        <>
          <video
            ref={blurRef}
            autoPlay
            muted
            playsInline
            aria-hidden="true"
            style={styles.blurredBg}
          />
          <video
            ref={mainVideoRef}
            autoPlay
            muted
            playsInline
            style={styles.video}
          />
          {broadcasterPaused && (
            <div style={styles.pausedOverlay}>
              <p style={styles.pausedText}>Host will be right back</p>
            </div>
          )}
        </>
      ) : (
        renderPlaceholder(broadcasterPaused ? 'Host will be right back' : 'Host camera is off')
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.gray[900],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  blurredBg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'blur(32px) brightness(0.7) saturate(1.1)',
    transform: 'scale(1.15)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    position: 'relative',
    zIndex: 1,
  },
  placeholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gray[900],
    position: 'relative',
    overflow: 'hidden',
  },
  thumbnailBg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'blur(32px) brightness(0.5) saturate(1.1)',
    transform: 'scale(1.15)',
    pointerEvents: 'none' as const,
  },
  placeholderText: {
    position: 'relative',
    color: '#ccc',
    zIndex: 1,
  },
  subtext: {
    position: 'relative',
    fontSize: '12px',
    color: '#aaa',
    marginTop: '8px',
    zIndex: 1,
  },
  pausedOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    zIndex: 2,
  },
  pausedText: {
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
  },
};
